import "./prism-retrieval.css"

import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import type { FilthSearchActivityState } from "../../utils/filth-search-activity"

import { usePrismSequence } from "@/features/chat/hooks/use-prism-sequence"
import {
  getPrismTurnMemory,
  usePrismNamedBefore,
  usePrismTurnKey,
} from "@/features/chat/hooks/use-prism-turn"
import { cn } from "@/lib/utils"

import {
  PRISM_FOCUS_LENSES,
  PRISM_FOCUS_PLACEHOLDER_LAYOUT,
  PRISM_FOCUS_PLACEHOLDERS,
  PRISM_FOCUS_SLOTS,
  type PrismSource,
} from "./prism-retrieval-constants"
import { PrismSourceChip } from "./prism-source-chip"

type PrismFocusPanelProps = {
  state: FilthSearchActivityState
  /** The cited FILTH sources named so far, in the answer's citation order. */
  sources: PrismSource[]
  isLive: boolean
  /** The answer's citation click handler; makes each named source a button. */
  onSourceClick?: (citationIndex: number) => void
}

const LENS_TINTS = ["bg-chart-1", "bg-chart-2", "bg-chart-3", "bg-chart-5", "bg-accent"] as const
const NO_STEPS: readonly number[] = []
const SLOTS_MEMORY = "focus:slots"

type SlotState = "pending" | "focus" | "named" | "recede"

/**
 * Fokuspanelen: a field of soft, textless shapes while Prism checks access.
 * At grounding, one evidence slot per reported evidence item (at most
 * `PRISM_FOCUS_SLOTS`) is pulled into focus (blur to sharp, Focus) while the
 * answer is written. Each cited FILTH source is then named in its slot, in
 * citation order, as soon as it is identified: the name crossfades in over
 * the slot on Guiding. When the turn is over, the slots nothing was cited
 * for recede and the panel settles into a row of just the named sources.
 *
 * The slots are fixed cells reserved from mount, so naming a source never
 * moves another, and the panel's height changes only when it settles. The
 * shapes and slots stand for no document and never carry text.
 */
export function PrismFocusPanel({ state, sources, isLive, onSourceClick }: PrismFocusPanelProps) {
  const { t } = useTranslation()
  const sequence = usePrismSequence(isLive, NO_STEPS, { memoryId: "focus" })
  const turnKey = usePrismTurnKey()
  // Named before the message remounted: already on screen, so no entrance.
  const namedBefore = usePrismNamedBefore()
  const settled = !sequence.running
  const hasSources = sources.length > 0
  const grounded = state.phase === "grounded"

  // Slots already pulled stay pulled for the rest of the turn, across a
  // remount of the message too, even if a later search reports less.
  const [carriedSlots] = useState(() =>
    sequence.mountedLive ? (getPrismTurnMemory(turnKey)?.values.get(SLOTS_MEMORY) ?? 0) : 0
  )
  const [keptSlots, setKeptSlots] = useState(carriedSlots)
  const groundedSlots = grounded ? Math.min(state.evidenceCount, PRISM_FOCUS_SLOTS) : 0
  const pulled = Math.max(keptSlots, groundedSlots)
  useEffect(() => {
    if (!sequence.running || pulled <= keptSlots) return
    setKeptSlots(pulled)
    getPrismTurnMemory(turnKey)?.values.set(SLOTS_MEMORY, pulled)
  }, [sequence.running, pulled, keptSlots, turnKey])

  let subtitle: string
  if (hasSources) {
    subtitle = t("activity_messages.filth.sourcesInFocus", {
      count: sources.length,
      defaultValue: "{{count}} sources in focus",
    })
  } else if (grounded && settled) {
    subtitle = t("activity_messages.filth.evidence", {
      count: state.evidenceCount,
      defaultValue: "{{count}} evidence items",
    })
  } else if (grounded) {
    subtitle = t("activity_messages.filth.writingAnswer", {
      count: state.evidenceCount,
      defaultValue: "Writing the answer · {{count}} evidence items",
    })
  } else {
    subtitle = t("activity_messages.filth.authorizing", {
      defaultValue: "Checking identity and access",
    })
  }

  // A settled panel from a finished turn has no field at all; the live turn
  // keeps it for one Focus beat so it can fade while the panel settles.
  const showField = !settled || sequence.motion
  // A live panel reserves every slot from mount; a finished turn draws only
  // its named sources.
  const slotCount = sequence.motion ? Math.max(PRISM_FOCUS_SLOTS, sources.length) : sources.length

  const slotState = (index: number): SlotState => {
    if (index < sources.length) return "named"
    if (settled) return "recede"
    return index < pulled ? "focus" : "pending"
  }

  return (
    <div
      data-prism-animated={sequence.motion}
      data-settled={settled}
      data-testid="prism-focus-panel"
      className={cn(
        "prism-settle bg-card relative overflow-hidden rounded-xl border",
        settled ? "h-[78px]" : "h-[124px]",
        sequence.motion && "prism-motion"
      )}
    >
      <div className="absolute top-3 left-3.5 z-10 flex items-center gap-2">
        <span className="type-headline text-[11px] leading-none">
          {t("activity_messages.filth.title", { defaultValue: "Prism" })}
        </span>
        <span className="text-muted-foreground text-xs leading-none" data-testid="prism-status">
          {subtitle}
        </span>
      </div>

      {showField && (
        <div
          aria-hidden="true"
          className={cn("prism-fade absolute inset-0", settled ? "opacity-0" : "opacity-100")}
          data-testid="prism-focus-field"
        >
          {PRISM_FOCUS_LENSES.map(([left, top, size], index) => (
            <span
              key={`lens-${index}`}
              style={{ left: `${left}%`, top: `${top}%`, width: size, height: size }}
              className={cn(
                "absolute rounded-full opacity-50 blur-[14px] dark:opacity-20",
                LENS_TINTS[index % LENS_TINTS.length]
              )}
            />
          ))}
          {PRISM_FOCUS_PLACEHOLDER_LAYOUT.slice(0, PRISM_FOCUS_PLACEHOLDERS).map(
            ([left, top, width], index) => (
              <span
                key={`placeholder-${index}`}
                className="prism-placeholder bg-card absolute h-6 -translate-x-1/2 -translate-y-1/2 rounded-full border"
                data-prism-placeholder=""
                data-recede={pulled > 0 || hasSources}
                style={{ left: `${left}%`, top: `${top}%`, width }}
              />
            )
          )}
        </div>
      )}

      {slotCount > 0 && (
        <ol
          className="hide-scrollbar absolute inset-x-3.5 bottom-3 z-10 flex gap-1.5 overflow-x-auto"
          aria-label={
            hasSources
              ? t("activity_messages.filth.sourcesInFocus", {
                  count: sources.length,
                  defaultValue: "{{count}} sources in focus",
                })
              : undefined
          }
        >
          {Array.from({ length: slotCount }, (_, index) => {
            const source = sources.at(index)
            const slot = slotState(index)
            return (
              <li
                key={`slot-${index}`}
                aria-hidden={source ? undefined : true}
                className="relative flex h-6 w-40 shrink-0"
                data-prism-slot={`slot-${index}`}
                data-slot-state={slot}
              >
                {sequence.motion && (
                  <span
                    className="prism-slot bg-card absolute inset-0 flex items-center rounded-full border px-2.5"
                    data-prism-evidence-slot=""
                    data-slot-state={slot}
                  >
                    <span className="bg-muted h-2 w-3/5 rounded-full" />
                  </span>
                )}
                {source && (
                  <PrismSourceChip
                    source={source}
                    className={cn(
                      "border-foreground bg-primary text-primary-foreground absolute inset-0 flex min-w-0 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium",
                      sequence.motion && index >= namedBefore && "prism-name-in"
                    )}
                    onOpen={onSourceClick}
                  >
                    <b className="font-bold tabular-nums">{source.n}</b>
                    <span className="truncate">{source.title}</span>
                  </PrismSourceChip>
                )}
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}
